import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { container } from 'tsyringe'
import { useTelegram } from './hooks/useTelegram'
import { ApiService } from './api/ApiService'
import { useModels } from './hooks/useModels'
import { ModelBrowser } from './components/ModelBrowser'
import { AgeWarningScreen } from './components/AgeWarningScreen'
import LoadingSpinner from './components/LoadingSpinner'
import { NotifyModal } from './components/NotifyModal'
import { ErrorScreen } from './components/ErrorScreen'
import type { LoginResponse } from './shared/types/LoginResponse'
import type { ModelLanguageCode } from './shared/types/Model'
import { useTranslation } from 'react-i18next'
import i18n from './i18n'
import './components/ErrorScreen.css'

export function App() {
  const { initData } = useTelegram()
  const [user, setUser] = useState<LoginResponse['user'] | null>(null)
  const [loginInfo, setLoginInfo] = useState<LoginResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const api = useMemo(() => container.resolve(ApiService), [])
  const { models, loading: modelsLoading, error: modelsError } = useModels(
    !!loginInfo,
    (loginInfo?.user.preferredLanguage ?? 'en') as ModelLanguageCode
  )
  const [isNewUser, setIsNewUser] = useState(false)
  const [showAwardModal, setShowAwardModal] = useState(false)
  const { t } = useTranslation()

  const hasValidated = useRef(false)

  useEffect(() => {
    if (loginInfo?.user.preferredLanguage) {
      i18n.changeLanguage(loginInfo.user.preferredLanguage)
    }
  }, [loginInfo])

  const handleValidation = useCallback(() => {
    if (!initData || hasValidated.current) return

    hasValidated.current = true

    api.validate(initData)
      .then((res) => {
        setUser(res.user)
        setLoginInfo(res)
        setIsNewUser(res.isNewUser)
        if (res.tokensAwardedToday) setShowAwardModal(true)
      })
      .catch((err) => {
        console.error('Ошибка валидации Telegram WebApp:', err)
        setError(t('tg_auth_error'))
        setUser(null)
      })
  }, [api, initData, t])

  useEffect(() => {
    handleValidation()
  }, [handleValidation])

  const handleRetry = () => {
    setError(null)
    hasValidated.current = false
    handleValidation()
  }

  const handleContactSupport = () => {
    // In a real implementation, this would open a support chat
    window.open('https://t.me/gleamai_support', '_blank')
  }

  if (!initData)
    return (
      <LoadingSpinner
        text={
          <>
            {t('telegram_only')}
            <br />
            <center>
              <a
                href="https://t.me/gleamai_bot?startapp"
                style={{ color: '#ff70b3', textDecoration: 'none' }}
              >
                @gleamai_bot
              </a>
            </center>
          </>
        }
      />
    )
    
  if (error)
    return (
      <ErrorScreen
        message={error}
        onRetry={handleRetry}
        onContactSupport={handleContactSupport}
      />
    )
    
  if (!user)
    return <LoadingSpinner text={t('authorizing')} />
    
  if (modelsLoading)
    return <LoadingSpinner text={t('fetching_models')} />
    
  if (modelsError)
    return (
      <ErrorScreen
        message={`Ошибка загрузки: ${modelsError}`}
        onRetry={() => {
          // Force re-fetch models
          window.location.reload()
        }}
      />
    )

  if (isNewUser) return <AgeWarningScreen onAgree={() => setIsNewUser(false)} />

  return (
    <>
      <ModelBrowser
        models={models}
        loginInfo={loginInfo}
        onLoginInfoChange={setLoginInfo}
      />
      {showAwardModal && (
        <NotifyModal
          text={t('tokens_awarded')}
          onClose={() => setShowAwardModal(false)}
        />
      )}
    </>
  )
}
